import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/free-solid-svg-icons';
import { PlanFeatureKey, PlanKey } from '../../service/plan/plan-service';
import './plan.css';

interface Props {
    feature: PlanFeatureKey;
    requiredPlan: PlanKey;
    currentPlan: PlanKey | null;
    /** Only an admin can open the Plano tab / change the plan. */
    canManagePlan: boolean;
    onClose: () => void;
    onSeePlans: () => void;
}

/**
 * "This is included in plan X" — opened when the user clicks a crowned
 * (locked) feature. One button goes to Settings → Plano (the upgrade screen).
 */
const PlanUpsellModal: React.FC<Props> = ({ feature, requiredPlan, currentPlan, canManagePlan, onClose, onSeePlans }) => {
    const { t } = useTranslation();
    return createPortal(
        <div className="gs-plan-upsell__backdrop" role="dialog" aria-modal="true" onClick={onClose} data-cy="plan-upsell">
            <div className="gs-plan-upsell" onClick={(e) => e.stopPropagation()}>
                <div className="gs-plan-upsell__icon"><FontAwesomeIcon icon={faCrown} /></div>
                <h3 className="gs-plan-upsell__title">{t(`plan.features.${feature}`)}</h3>
                <p className="gs-plan-upsell__text">
                    {t('plan.upsell.includedIn', { plan: t(`plan.names.${requiredPlan}`) })}
                    {currentPlan && (
                        <> {t('plan.upsell.yourPlan', { plan: t(`plan.names.${currentPlan}`) })}</>
                    )}
                </p>
                <p className="gs-plan-upsell__hint">{t(`plan.featureHints.${feature}`)}</p>
                {!canManagePlan && (
                    <p className="gs-plan-upsell__hint">{t('plan.upsell.askAdmin')}</p>
                )}
                <div className="gs-plan-upsell__actions">
                    <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                        {t('common.close', 'Fechar')}
                    </button>
                    {canManagePlan && (
                        <button type="button" className="btn btn-primary" onClick={onSeePlans} data-cy="plan-upsell-see-plans">
                            {t('plan.upsell.seePlans')}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default PlanUpsellModal;
